const pick = (raw, camel, pascal) => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  if (raw[camel] !== undefined) {
    return raw[camel];
  }

  if (raw[pascal] !== undefined) {
    return raw[pascal];
  }

  return undefined;
};

const toBoolean = (value, fallback) => {
  if (value === undefined || value === null) {
    return fallback;
  }

  return Boolean(value);
};

const toNullableNumber = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const normalizePlanKind = (value) => {
  if (value === 2 || value === '2') {
    return 'pro';
  }

  if (value === 1 || value === '1') {
    return 'free';
  }

  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'pro') {
    return 'pro';
  }

  if (normalized === 'free') {
    return 'free';
  }

  return null;
};

export const DEFAULT_FREE_ENTITLEMENTS = {
  effectivePlan: 'free',
  isEffectivePro: false,
  allowsExtraStore: false,
  allowsExtraStores: false,
  allowsExtraTeamMember: false,
  allowsExtraTeamMembers: false,
  allowsProducts: false,
  allowsMemberships: false,
  allowsServicePackages: false,
  allowsPayroll: false,
  allowsRoles: false,
  allowsProfessionalShare: false,
  allowsWaitlist: false,
  allowsAdditionalHours: false,
  allowsSms: false,
  allowsWhatsApp: false,
  allowsCustomBranding: false,
  allowsConsentForms: false,
  allowsServiceCombos: false,
  allowsExpenses: false,
  restrictsToOwnerProfessional: true,
  isPrioritySupport: false,
  isLapsedPro: false,
  isInPaymentGrace: false,
  showPaymentFailedBanner: false,
  showReactivateNudge: false,
  reactivateNudgeStage: null,
  proLapsedAtUtc: null,
  cancelAtPeriodEnd: false,
  cancelsAtUtc: null,
};

export const DEFAULT_PRO_ENTITLEMENTS = {
  effectivePlan: 'pro',
  isEffectivePro: true,
  allowsExtraStore: true,
  allowsExtraStores: true,
  allowsExtraTeamMember: true,
  allowsExtraTeamMembers: true,
  allowsProducts: true,
  allowsMemberships: true,
  allowsServicePackages: true,
  allowsPayroll: true,
  allowsRoles: true,
  allowsProfessionalShare: true,
  allowsWaitlist: true,
  allowsAdditionalHours: true,
  allowsSms: true,
  allowsWhatsApp: true,
  allowsCustomBranding: true,
  allowsConsentForms: true,
  allowsServiceCombos: true,
  allowsExpenses: true,
  restrictsToOwnerProfessional: false,
  isPrioritySupport: true,
  isLapsedPro: false,
  isInPaymentGrace: false,
  showPaymentFailedBanner: false,
  showReactivateNudge: false,
  reactivateNudgeStage: null,
  proLapsedAtUtc: null,
  cancelAtPeriodEnd: false,
  cancelsAtUtc: null,
};

const withAliases = (entitlements) => ({
  ...entitlements,
  allowsExtraStore: Boolean(entitlements.allowsExtraStore ?? entitlements.allowsExtraStores),
  allowsExtraStores: Boolean(entitlements.allowsExtraStores ?? entitlements.allowsExtraStore),
  allowsExtraTeamMember: Boolean(entitlements.allowsExtraTeamMember ?? entitlements.allowsExtraTeamMembers),
  allowsExtraTeamMembers: Boolean(entitlements.allowsExtraTeamMembers ?? entitlements.allowsExtraTeamMember),
});

export const normalizeEntitlements = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_FREE_ENTITLEMENTS };
  }

  const explicitPlan = normalizePlanKind(pick(raw, 'effectivePlan', 'EffectivePlan'));
  const isProFlag = toBoolean(pick(raw, 'isEffectivePro', 'IsEffectivePro'), explicitPlan === 'pro');
  const effectivePlan = explicitPlan || (isProFlag ? 'pro' : 'free');
  const defaults = effectivePlan === 'pro' ? DEFAULT_PRO_ENTITLEMENTS : DEFAULT_FREE_ENTITLEMENTS;

  const allowsExtraStores = toBoolean(
    pick(raw, 'allowsExtraStores', 'AllowsExtraStores') ?? pick(raw, 'allowsExtraStore', 'AllowsExtraStore'),
    defaults.allowsExtraStores,
  );
  const allowsExtraTeamMembers = toBoolean(
    pick(raw, 'allowsExtraTeamMembers', 'AllowsExtraTeamMembers')
      ?? pick(raw, 'allowsExtraTeamMember', 'AllowsExtraTeamMember'),
    defaults.allowsExtraTeamMembers,
  );
  const reactivateNudgeStage = toNullableNumber(pick(raw, 'reactivateNudgeStage', 'ReactivateNudgeStage'));
  const isLapsedPro = toBoolean(pick(raw, 'isLapsedPro', 'IsLapsedPro'), defaults.isLapsedPro);
  const showReactivateNudge = toBoolean(
    pick(raw, 'showReactivateNudge', 'ShowReactivateNudge'),
    isLapsedPro && reactivateNudgeStage != null && reactivateNudgeStage >= 1,
  );

  return withAliases({
    effectivePlan,
    isEffectivePro: isProFlag || effectivePlan === 'pro',
    allowsExtraStores,
    allowsExtraStore: allowsExtraStores,
    allowsExtraTeamMembers,
    allowsExtraTeamMember: allowsExtraTeamMembers,
    allowsProducts: toBoolean(pick(raw, 'allowsProducts', 'AllowsProducts'), defaults.allowsProducts),
    allowsMemberships: toBoolean(pick(raw, 'allowsMemberships', 'AllowsMemberships'), defaults.allowsMemberships),
    allowsServicePackages: toBoolean(
      pick(raw, 'allowsServicePackages', 'AllowsServicePackages'),
      defaults.allowsServicePackages,
    ),
    allowsPayroll: toBoolean(pick(raw, 'allowsPayroll', 'AllowsPayroll'), defaults.allowsPayroll),
    allowsRoles: toBoolean(pick(raw, 'allowsRoles', 'AllowsRoles'), defaults.allowsRoles),
    allowsProfessionalShare: toBoolean(
      pick(raw, 'allowsProfessionalShare', 'AllowsProfessionalShare'),
      defaults.allowsProfessionalShare,
    ),
    allowsWaitlist: toBoolean(pick(raw, 'allowsWaitlist', 'AllowsWaitlist'), defaults.allowsWaitlist),
    allowsAdditionalHours: toBoolean(
      pick(raw, 'allowsAdditionalHours', 'AllowsAdditionalHours'),
      defaults.allowsAdditionalHours,
    ),
    allowsSms: toBoolean(pick(raw, 'allowsSms', 'AllowsSms'), defaults.allowsSms),
    allowsWhatsApp: toBoolean(pick(raw, 'allowsWhatsApp', 'AllowsWhatsApp'), defaults.allowsWhatsApp),
    allowsCustomBranding: toBoolean(
      pick(raw, 'allowsCustomBranding', 'AllowsCustomBranding'),
      defaults.allowsCustomBranding,
    ),
    allowsConsentForms: toBoolean(
      pick(raw, 'allowsConsentForms', 'AllowsConsentForms'),
      defaults.allowsConsentForms,
    ),
    allowsServiceCombos: toBoolean(
      pick(raw, 'allowsServiceCombos', 'AllowsServiceCombos'),
      defaults.allowsServiceCombos,
    ),
    allowsExpenses: toBoolean(pick(raw, 'allowsExpenses', 'AllowsExpenses'), defaults.allowsExpenses),
    restrictsToOwnerProfessional: toBoolean(
      pick(raw, 'restrictsToOwnerProfessional', 'RestrictsToOwnerProfessional'),
      defaults.restrictsToOwnerProfessional,
    ),
    isPrioritySupport: toBoolean(pick(raw, 'isPrioritySupport', 'IsPrioritySupport'), defaults.isPrioritySupport),
    isLapsedPro,
    isInPaymentGrace: toBoolean(pick(raw, 'isInPaymentGrace', 'IsInPaymentGrace'), defaults.isInPaymentGrace),
    showPaymentFailedBanner: toBoolean(
      pick(raw, 'showPaymentFailedBanner', 'ShowPaymentFailedBanner'),
      defaults.showPaymentFailedBanner,
    ),
    showReactivateNudge,
    reactivateNudgeStage,
    proLapsedAtUtc: pick(raw, 'proLapsedAtUtc', 'ProLapsedAtUtc') ?? null,
    cancelAtPeriodEnd: toBoolean(pick(raw, 'cancelAtPeriodEnd', 'CancelAtPeriodEnd'), defaults.cancelAtPeriodEnd),
    cancelsAtUtc: pick(raw, 'cancelsAtUtc', 'CancelsAtUtc') ?? null,
  });
};

export const isEffectivePro = (entitlements) => Boolean(entitlements?.isEffectivePro);

export const isFreeOrLapsed = (entitlements) => Boolean(entitlements) && !isEffectivePro(entitlements);

export const isPublicFreePlan = (overviewOrPlan) => {
  const rawPlan = overviewOrPlan && typeof overviewOrPlan === 'object'
    ? (overviewOrPlan.effectivePlan ?? overviewOrPlan.EffectivePlan)
    : overviewOrPlan;

  return normalizePlanKind(rawPlan) === 'free';
};

export const shouldSkipProfessionalsStep = (professionalsWhoCanDoAll) => (
  Array.isArray(professionalsWhoCanDoAll) && professionalsWhoCanDoAll.length === 1
);

export const isExtraHourSlot = (slot) => Boolean(
  slot?.requiresConfirmation
  || slot?.RequiresConfirmation
  || slot?.hasAdditionalCost
  || slot?.HasAdditionalCost,
);

export const filterBookingSlotsForPlan = (slots, hideExtraHourSlots) => {
  if (!Array.isArray(slots)) {
    return [];
  }

  if (!hideExtraHourSlots) {
    return slots;
  }

  return slots.filter((slot) => !isExtraHourSlot(slot));
};

export const hasEntitlement = (entitlements, flag) => Boolean(entitlements?.[flag]);
